import React, { useState } from "react";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Account,
  useDeleteAccount,
  useListAndSearchAllAccounts,
} from "@rentablo/api/apiv1";
import { ConfirmDeleteDialog } from "@components";
import { ContraAccountRow } from "./AdditionalContraAccounts/ContraAccountRow";

interface DeleteAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
  accountId: number;
  type: "account" | "depot";
}

export const DeleteAccountDialog = ({
  isOpen,
  onClose,
  accountId,
  type,
}: DeleteAccountDialogProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDepot = type === "depot";

  const [selectedIds, setSelectedIds] = useState<number[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { data: accountList } = useListAndSearchAllAccounts(undefined, {
    enabled: isDepot && isOpen,
  });
  const { mutateAsync: deleteAccount } = useDeleteAccount();

  const accounts = accountList?.accounts ?? [];
  const depot = accounts.find((item) => item.id === accountId);
  const additionalIds = (depot?.additionalContraAccountIds ?? []).filter(
    (id) => id !== depot?.contraAccountId
  );
  const referenceAccounts = [depot?.contraAccountId, ...additionalIds]
    .filter((id): id is number => Boolean(id))
    .map((id) => accounts.find((item) => item.id === id))
    .filter((item): item is Account => Boolean(item));

  const hasAdditional = additionalIds.length > 0;
  const primary = referenceAccounts.find(
    (refAccount) => refAccount.id === depot?.contraAccountId
  );

  const isDeletable = (refAccount: Account) =>
    refAccount.depotIds?.length === 1;

  // Default: every deletable reference account is selected until the user
  // changes the selection (selectedIds === null means "use defaults").
  const deletableIds = referenceAccounts
    .filter(isDeletable)
    .map((refAccount) => refAccount.id);
  const selected = selectedIds ?? deletableIds;

  const toggle = (id: number) =>
    setSelectedIds((ids) => {
      const base = ids ?? deletableIds;
      return base.includes(id)
        ? base.filter((current) => current !== id)
        : [...base, id];
    });

  const deleteCopy = `createAndEditAccount.dangerZone.deleteAccount.${type}`;

  const handleDelete = async () => {
    setIsDeleting(true);
    setErrorMessage(undefined);
    try {
      const deleteContraAccount = depot?.contraAccountId
        ? selected.includes(depot.contraAccountId)
        : false;
      await deleteAccount({ id: accountId, deleteContraAccount });

      const additionalToDelete = referenceAccounts.filter(
        (refAccount) =>
          refAccount.id !== depot?.contraAccountId &&
          selected.includes(refAccount.id)
      );
      await Promise.all(
        additionalToDelete.map((refAccount) =>
          deleteAccount({ id: refAccount.id })
        )
      );

      navigate(isDepot ? "/depots-accounts" : "/accounts");
    } catch (error) {
      // A delete failed (possibly after the depot itself was already removed);
      // keep the dialog open and surface the error instead of navigating away.
      const message = (error as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setErrorMessage(message ?? t("form.errors.somethingWentWrong"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ConfirmDeleteDialog
      confirmButtonText={t(`${deleteCopy}.button`)}
      cancelButtonText={t("createAndEditAccount.deleteDialog.close")}
      isOpen={isOpen}
      onClose={onClose}
      onDelete={handleDelete}
      isDeleteRequestLoading={isDeleting}
      title={t(`${deleteCopy}.confirmTitle`)}
      errorMessage={errorMessage}
      testId="delete-account-confirmation-dialog"
    >
      {isDepot && hasAdditional && (
        <Box mt={1}>
          <Typography variant="body1" color="textSecondary" mb={8}>
            {t(
              "createAndEditAccount.dangerZone.deleteAccount.referenceAccountsLabel"
            )}
          </Typography>
          <Box display="flex" flexDirection="column" gap={4}>
            {referenceAccounts.map((refAccount) => {
              const deletable = isDeletable(refAccount);
              return (
                <Box
                  key={refAccount.id}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ opacity: deletable ? 1 : 0.6 }}
                >
                  <Checkbox
                    sx={{ p: 0 }}
                    checked={deletable && selected.includes(refAccount.id)}
                    onChange={() => toggle(refAccount.id)}
                    disabled={!deletable}
                    data-testid={`delete-reference-${refAccount.id}`}
                  />
                  <Box flex={1} minWidth={0}>
                    <ContraAccountRow
                      accountName={refAccount.name}
                      iban={refAccount.iban}
                      bankImage={refAccount.iconUrl}
                      type={refAccount.typeFromBank}
                      isPrimary={refAccount.id === depot?.contraAccountId}
                    />
                    {!deletable && (
                      <Typography variant="caption" color="textSecondary">
                        {t(
                          "createAndEditAccount.dangerZone.deleteAccount.referenceBoundMessage"
                        )}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {isDepot && !hasAdditional && primary && (
        <Box mt={3} display="flex" alignItems="center">
          <Checkbox
            sx={{ paddingLeft: 0 }}
            checked={isDeletable(primary) && selected.includes(primary.id)}
            onChange={() => toggle(primary.id)}
            disabled={!isDeletable(primary)}
            data-testid={`delete-reference-${primary.id}`}
          />
          <Typography color={theme.palette.text.secondary}>
            {t(
              "createAndEditAccount.dangerZone.deleteAccount.deleteContraAccount"
            )}
          </Typography>
        </Box>
      )}
    </ConfirmDeleteDialog>
  );
};
